import { useEffect, useRef, useState, type AnchorHTMLAttributes } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  CheckCircle2,
  ClipboardCheck,
  ClipboardPen,
  Clock,
  Download,
  ExternalLink,
  FileText,
  Lightbulb,
  ListChecks,
  PackageOpen,
  Presentation,
  Target,
  Video,
} from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import { ApiError, api } from '@/lib/api';
import type { ResourceType, SessionDetail } from '@/types/api';
import { cn, downloadBlob, fileSize, formatDate, formatMinutes } from '@/lib/utils';
import { Markdown } from '@/components/Markdown';
import { VideoPlayer } from '@/features/learning/VideoPlayer';
import { readingMinutes, tableOfContents } from '@/features/learning/outline';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  LoadingState,
  useToast,
} from '@/components/ui';

const RESOURCE_ICONS: Record<ResourceType, typeof FileText> = {
  PDF: FileText,
  DOCUMENT: FileText,
  SLIDE: Presentation,
  VIDEO: Video,
  AUDIO: Video,
  LINK: ExternalLink,
};

/** Intervalle d'envoi du temps passe sur la session (secondes). */
const HEARTBEAT_SECONDS = 30;

/** Une etape du deroule de la seance. | One step of the session outline. */
interface OutlineStep {
  /** Ancre de la section correspondante sur la page. */
  anchor: string;
  icon: typeof FileText;
  label: string;
  detail: string;
  minutes?: number;
}

export function SessionPage() {
  const { sessionId = '' } = useParams();
  const { t, language, pick } = useLanguage();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const elapsedRef = useRef(0);

  const [scrolledToEnd, setScrolledToEnd] = useState(false);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['catalog', 'session', sessionId],
    queryFn: () => api.get<{ session: SessionDetail }>(`/catalog/sessions/${sessionId}`),
    enabled: Boolean(sessionId),
    retry: (count, cause) => !(cause instanceof ApiError && cause.status === 403) && count < 2,
  });

  const track = useMutation({
    mutationFn: (payload: {
      timeSpentSeconds: number;
      progressPercent?: number;
      completed?: boolean;
      lastPosition?: number;
    }) => api.post(`/progress/sessions/${sessionId}`, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['progress', 'overview'] });
      void queryClient.invalidateQueries({ queryKey: ['catalog'] });
    },
  });

  // Comptage du temps de consultation, envoye periodiquement puis a la sortie.
  useEffect(() => {
    if (!sessionId) return undefined;

    const tick = setInterval(() => {
      elapsedRef.current += HEARTBEAT_SECONDS;
      track.mutate({ timeSpentSeconds: HEARTBEAT_SECONDS, progressPercent: 25 });
    }, HEARTBEAT_SECONDS * 1000);

    return () => clearInterval(tick);
    // `track` est stable pour un identifiant de session donne.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Detection de la lecture complete du contenu.
  useEffect(() => {
    const onScroll = () => {
      const reachedEnd = window.innerHeight + window.scrollY >= document.body.offsetHeight - 240;
      if (reachedEnd) setScrolledToEnd(true);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [sessionId]);

  if (isLoading) return <LoadingState />;

  if (isError) {
    const forbidden = error instanceof ApiError && error.code === 'NOT_ENROLLED';
    return <ErrorState message={forbidden ? t('learn.notEnrolledError') : undefined} onRetry={() => void refetch()} />;
  }
  if (!data) return <ErrorState onRetry={() => void refetch()} />;

  const session = data.session;
  const completed = session.progress?.status === 'COMPLETED';
  const content = language === 'en' ? session.contentEn : session.contentFr;
  const resources = session.resources;
  const objectives = language === 'en' ? session.objectivesEn : session.objectivesFr;
  const keyPoints = language === 'en' ? session.keyPointsEn : session.keyPointsFr;
  const assignments = session.assignments ?? [];
  const scormPackages = session.scormPackages ?? [];
  const contents = tableOfContents(content);
  const courseMinutes = readingMinutes(content);

  /**
   * Deroule de la seance : ce que l'agent va faire, dans l'ordre, avec la
   * duree annoncee quand elle est connue. Chaque etape renvoie a sa section.
   * Session outline: what the officer will do, in order.
   */
  const steps: OutlineStep[] = [];
  if (session.videoUrl) {
    steps.push({
      anchor: 'seance-video',
      icon: Video,
      label: t('learn.video'),
      detail: t('learn.stepVideo'),
      minutes: session.videoDurationSeconds ? Math.max(1, Math.round(session.videoDurationSeconds / 60)) : undefined,
    });
  }
  steps.push({
    anchor: 'seance-cours',
    icon: BookOpen,
    label: t('learn.course'),
    detail: t('learn.stepCourse', { count: contents.length }),
    minutes: courseMinutes,
  });
  if (resources.length > 0) {
    steps.push({
      anchor: 'seance-ressources',
      icon: FileText,
      label: t('learn.resources'),
      detail: t('learn.stepResources', { count: resources.length }),
    });
  }
  if (scormPackages.length > 0) {
    steps.push({
      anchor: 'seance-interactif',
      icon: PackageOpen,
      label: t('learn.scormModules'),
      detail: t('learn.stepScorm', { count: scormPackages.length }),
    });
  }
  if (assignments.length > 0) {
    steps.push({
      anchor: 'seance-devoirs',
      icon: ClipboardPen,
      label: t('learn.assignments'),
      detail: t('learn.stepAssignments', { count: assignments.length }),
    });
  }
  if (session.quizzes.length > 0) {
    steps.push({
      anchor: 'seance-evaluation',
      icon: ClipboardCheck,
      label: t('learn.takeQuiz'),
      detail: t('quiz.questionsCount', { count: session.quizzes[0]?._count?.questions ?? 0 }),
      minutes: session.quizzes[0]?.timeLimitMinutes ?? undefined,
    });
  }

  /**
   * Recupere un support heberge par la plateforme et le remet au navigateur.
   * Fetches a platform-hosted handout and hands it to the browser.
   */
  const download = async (resource: { url: string; titleFr: string; titleEn: string }) => {
    try {
      const blob = await api.blob(resource.url.replace(/^\/api\/v1/, ''));
      downloadBlob(blob, `${pick(resource, 'title')}.pdf`);
    } catch (cause) {
      toast.error(cause instanceof ApiError ? cause.localized() : t('errors.generic'));
    }
  };

  /** Memorise la position quittee pour reprendre la video au meme endroit. */
  const savePosition = (seconds: number) => {
    track.mutate({ timeSpentSeconds: 0, lastPosition: seconds });
  };

  const complete = async () => {
    await track.mutateAsync({ timeSpentSeconds: 0, completed: true });
    toast.success(t('learn.sessionCompleted'));
    if (session.next) navigate(`/learn/sessions/${session.next.id}`);
  };

  return (
    <div className="mx-auto max-w-4xl">
      {/* Fil d'Ariane */}
      <nav className="mb-4 flex flex-wrap items-center gap-1.5 text-xs text-ink-muted" aria-label="breadcrumb">
        <Link to="/learn" className="hover:text-primary">
          {pick(session.module.program, 'title')}
        </Link>
        <span aria-hidden>/</span>
        <span className="text-ink">
          {t('learn.module')} {session.module.order} — {pick(session.module, 'title')}
        </span>
      </nav>

      <header className="mb-6 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="primary">
            {t('learn.session')} {session.module.order}.{session.order}
          </Badge>
          <Badge tone="neutral">
            <Clock className="h-3 w-3" aria-hidden />
            {formatMinutes(session.durationMinutes, language)}
          </Badge>
          {completed ? (
            <Badge tone="success" dot>
              {t('learn.completed')}
            </Badge>
          ) : null}
        </div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl">{pick(session, 'title')}</h1>
        <p className="text-sm text-ink-muted">{pick(session, 'summary')}</p>
      </header>

      {objectives.length > 0 ? (
        <Card className="mb-6" id="seance-objectifs">
          <CardHeader title={t('learn.objectives')} description={t('learn.objectivesHint')} />
          <CardBody>
            <ul className="space-y-2.5">
              {objectives.map((objective) => (
                <li key={objective} className="flex items-start gap-3 text-sm text-ink">
                  <Target className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
                  <span>{objective}</span>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      ) : null}

      <Card className="mb-6">
        <CardHeader title={t('learn.outline')} description={t('learn.outlineHint')} />
        <CardBody>
          <ol className="space-y-1">
            {steps.map((step, position) => (
              <li key={step.anchor}>
                <a
                  href={`#${step.anchor}`}
                  className="flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-surface-muted"
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-surface-muted text-xs font-semibold tabular-nums text-ink-muted">
                    {position + 1}
                  </span>
                  <step.icon className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-ink">{step.label}</span>
                    <span className="block truncate text-xs text-ink-subtle">{step.detail}</span>
                  </span>
                  {step.minutes ? (
                    <span className="shrink-0 text-xs tabular-nums text-ink-subtle">
                      {formatMinutes(step.minutes, language)}
                    </span>
                  ) : null}
                </a>
              </li>
            ))}
          </ol>
          {/* Les durees des etapes sont indicatives : le temps de lecture est
              estime, l'echange et les exercices ne le sont pas. */}
          <p className="mt-3 border-t border-border pt-3 text-xs text-ink-subtle">
            {t('learn.outlineTotal', { duration: formatMinutes(session.durationMinutes, language) })}
          </p>
        </CardBody>
      </Card>

      {session.videoUrl ? (
        <Card className="mb-6" id="seance-video">
          <CardHeader title={t('learn.video')} />
          <CardBody>
            <VideoPlayer
              src={session.videoUrl}
              captionsUrl={language === 'en' ? session.videoCaptionsUrlEn : session.videoCaptionsUrlFr}
              transcript={pick(session, 'transcript') || null}
              startAt={session.progress?.lastPosition ?? 0}
              onPosition={(seconds) => savePosition(seconds)}
            />
          </CardBody>
        </Card>
      ) : null}

      <Card className="mb-6" id="seance-cours">
        <CardBody>
          {contents.length > 2 ? (
            <nav
              aria-label={t('learn.contents')}
              className="mb-6 rounded-xl border border-border bg-surface-muted/60 p-4"
            >
              <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-ink-subtle">
                <ListChecks className="h-3.5 w-3.5" aria-hidden />
                {t('learn.contents')}
                <span className="font-normal normal-case tracking-normal">
                  · {t('learn.readingTime', { minutes: courseMinutes })}
                </span>
              </p>
              <ol className="space-y-1">
                {contents.map((entry) => (
                  <li key={entry.id} className={cn('text-sm', entry.level === 3 && 'ps-4')}>
                    <a href={`#${entry.id}`} className="text-ink-muted hover:text-primary">
                      {entry.title}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          ) : null}

          <Markdown content={content} anchors />
        </CardBody>
      </Card>

      {keyPoints.length > 0 ? (
        <Card className="mb-6 border-accent/40" id="seance-retenir">
          <CardHeader title={t('learn.keyPoints')} description={t('learn.keyPointsHint')} />
          <CardBody>
            <ul className="space-y-2.5">
              {keyPoints.map((point) => (
                <li key={point} className="flex items-start gap-3 text-sm text-ink">
                  <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      ) : null}

      {resources.length > 0 ? (
        <Card className="mb-6" id="seance-ressources">
          <CardHeader title={t('learn.resources')} description={t('learn.resourcesHint')} />
          <CardBody>
            <ul className="grid gap-2 sm:grid-cols-2">
              {resources.map((resource) => {
                const Icon = RESOURCE_ICONS[resource.type];
                // Un support heberge par la plateforme vit derriere une route
                // authentifiee : un lien nu partirait sans jeton et reviendrait
                // refuse. Il est donc recupere puis remis au navigateur.
                const hosted = resource.url.startsWith('/api/');
                const anchorProps: AnchorHTMLAttributes<HTMLAnchorElement> = hosted
                  ? {
                      href: resource.url,
                      onClick: (event) => {
                        event.preventDefault();
                        void download(resource);
                      },
                    }
                  : { href: resource.url, target: '_blank', rel: 'noopener noreferrer' };

                return (
                  <li key={resource.id} className="min-w-0">
                    <a
                      {...anchorProps}
                      className="flex items-center gap-3 rounded-xl border border-border p-3 transition-colors hover:border-primary/40 hover:bg-surface-muted"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
                        <Icon className="h-4.5 w-4.5" aria-hidden />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-ink">{pick(resource, 'title')}</p>
                        <p className="text-xs text-ink-subtle">
                          {resource.type}
                          {resource.fileSizeBytes ? ` · ${fileSize(resource.fileSizeBytes, language)}` : ''}
                        </p>
                      </div>
                      <Download className="h-4 w-4 shrink-0 text-ink-subtle" aria-hidden />
                    </a>
                  </li>
                );
              })}
            </ul>
          </CardBody>
        </Card>
      ) : null}

      {scormPackages.length > 0 ? (
        <Card className="mb-6" id="seance-interactif">
          <CardHeader title={t('learn.scormModules')} description={t('learn.scormHint')} />
          <CardBody>
            <ul className="grid gap-2">
              {scormPackages.map((scormPackage) => (
                <li key={scormPackage.id} className="min-w-0">
                  <Link
                    to={`/learn/scorm/${scormPackage.id}`}
                    className="flex items-center gap-3 rounded-xl border border-border p-3 transition-colors hover:border-primary/40 hover:bg-surface-muted"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
                      <PackageOpen className="h-4.5 w-4.5" aria-hidden />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-ink">{pick(scormPackage, 'title')}</p>
                      <p className="text-xs text-ink-subtle">
                        SCORM {scormPackage.version === 'V2004' ? '2004' : '1.2'}
                      </p>
                    </div>
                    <ArrowRight className="h-4 w-4 shrink-0 text-ink-subtle" aria-hidden />
                  </Link>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      ) : null}

      {assignments.length > 0 ? (
        <Card className="mb-6" id="seance-devoirs">
          <CardHeader title={t('learn.assignments')} description={t('learn.assignmentsHint')} />
          <CardBody>
            <ul className="grid gap-2">
              {assignments.map((assignment) => (
                <li key={assignment.id} className="min-w-0">
                  <Link
                    to="/assignments"
                    className="flex items-center gap-3 rounded-xl border border-border p-3 transition-colors hover:border-primary/40 hover:bg-surface-muted"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
                      <ClipboardPen className="h-4.5 w-4.5" aria-hidden />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-ink">{pick(assignment, 'title')}</p>
                      <p className="text-xs text-ink-subtle">
                        {t('assignments.outOf', { points: assignment.maxPoints })}
                        {assignment.dueAt
                          ? ` · ${t('assignments.dueOn', { date: formatDate(assignment.dueAt, language) })}`
                          : ''}
                      </p>
                    </div>
                    <ArrowRight className="h-4 w-4 shrink-0 text-ink-subtle" aria-hidden />
                  </Link>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      ) : null}

      {session.quizzes.length > 0 ? (
        <Card className="mb-6 border-accent/40" id="seance-evaluation">
          <CardBody className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent-soft text-accent">
                <ClipboardCheck className="h-5 w-5" aria-hidden />
              </span>
              <div>
                <p className="font-semibold text-ink">{pick(session.quizzes[0], 'title')}</p>
                <p className="text-sm text-ink-muted">
                  {t('quiz.questionsCount', { count: session.quizzes[0]?._count?.questions ?? 0 })} ·{' '}
                  {t('quiz.passingScore')} : {session.quizzes[0]?.passingScore}%
                </p>
              </div>
            </div>
            <Link to={`/quizzes/${session.quizzes[0]?.id}`} className="shrink-0">
              <Button variant="accent">{t('learn.takeQuiz')}</Button>
            </Link>
          </CardBody>
        </Card>
      ) : null}

      {/* Validation et navigation */}
      <div className="sticky bottom-0 -mx-4 border-t border-border bg-surface/95 px-4 py-3 backdrop-blur sm:mx-0 sm:rounded-2xl sm:border">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          {session.previous ? (
            <Link to={`/learn/sessions/${session.previous.id}`}>
              <Button variant="ghost" size="sm" icon={<ArrowLeft className="h-4 w-4" />}>
                <span className="truncate">{t('learn.previousSession')}</span>
              </Button>
            </Link>
          ) : (
            <Link to="/learn">
              <Button variant="ghost" size="sm" icon={<ArrowLeft className="h-4 w-4" />}>
                {t('learn.backToPath')}
              </Button>
            </Link>
          )}

          <div className="flex items-center gap-2">
            {completed ? (
              <Badge tone="success" dot>
                {t('learn.completed')}
              </Badge>
            ) : (
              <Button
                onClick={() => void complete()}
                loading={track.isPending}
                icon={<CheckCircle2 className="h-4 w-4" />}
                className={cn(!scrolledToEnd && 'opacity-90')}
              >
                {t('learn.markComplete')}
              </Button>
            )}

            {session.next ? (
              <Link to={`/learn/sessions/${session.next.id}`}>
                <Button variant="outline" size="sm">
                  <span className="truncate">{t('learn.nextSession')}</span>
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </Button>
              </Link>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
